/**
 * @module app.shared
 */

import { IApiSessionLocation, IApiSessionUserContext, IApiSessionTenant, IApiSessionHomePage, IApiSessionUser, IApiSessionApplication, IApiSessionSignOnProvider } from 'app/shared/lib/api/api-session-types';
import { ISessionLocation, ISessionUserContext, ISessionTenant, ISessionHomePage, ISessionUser, ISessionApplication, ISessionSignOnProvider } from 'app/shared/lib/session/session-types';
import { emptyHomePage } from 'app/shared/lib/session/session-defaults';

export function mapApiLocation(location: IApiSessionLocation | null): ISessionLocation | null {
    if(!location) { return null; }
    return {
        isSelected: !!location.id,
        id: location.id || '',
        code: location.code || '',
        description: location.description || ''
    };
}

export function mapApiUserContext(userContext: IApiSessionUserContext | null): ISessionUserContext | null {
    if(!userContext) { return null; }
    return {
        isSelected: !!userContext.id,
        id: userContext.id || '',
        description: userContext.description || '',
        attachedToCode: userContext.attachedTo?.type?.code || '',
        attachedToId: userContext.attachedTo?.id || ''
    };
}

export function mapApiApplication(application: IApiSessionApplication | null): ISessionApplication | null {
    if(!application) { return null; }
    return {
        id: application.id || '',
        code: application.code || ''
    };
}

export function mapApiTenant(tenant: IApiSessionTenant | null): ISessionTenant | null {
    if(!tenant) { return null; }
    return {
        isSelected: !!tenant.id,
        id: tenant.id || '',
        code: tenant.code || '',
        description: tenant.description || '',
        twoFactorEnabled: tenant.twoFactor && tenant.twoFactor.isEnabled || false,
        twoFactorPersist: tenant.twoFactor && tenant.twoFactor.allowPersistence || false,
        timeZone: tenant.timeZone && tenant.timeZone.code || '',
        language: '',
        locations: (tenant.locations || []).map(location => mapApiLocation(location)!),
        userContexts: (tenant.userContexts || []).map(userContext => mapApiUserContext(userContext)!)
    };
}

export function mapApiHomePage(homePage: IApiSessionHomePage | null): ISessionHomePage | null {
    if(!homePage) { return null; }
    return {
        id: homePage.id || '',
        code: homePage.code || '',
        description: homePage.description || '',
        href: homePage.href || ''
    };
}

export function mapApiSignOnProvider(provider: IApiSessionSignOnProvider | null): ISessionSignOnProvider | null {
    if(!provider) { return null; }
    return {
        id: provider.id || '',
        code: provider.code?.toLowerCase() || '',
        description: provider.description || ''
    };
}

export function mapApiUser(user: IApiSessionUser | null): ISessionUser | null {
    if(!user) { return null; }
    return {
        isLoggedIn: true,
        id: user.id,
        hasGlobalAccess: user.login?.hasGlobalAccess || false,
        loginName: user.login && user.login.loginName || '',
        description: user.description || '',
        name: {
            first: user.name && user.name.first || '',
            middle: user.name && user.name.middle || '',
            last: user.name && user.name.last || '',
            full: user.name && user.name.full || '',
            display: user.name && user.name.display || '',
        },
        emailAddress: user.emailAddress || '',
        department: user.department || '',
        title: user.title || '',
        timeZone: user.displayTimeZone && user.displayTimeZone.code || '',
        language: '',
        homePage: mapApiHomePage(user.context && user.context.homePage || null) || emptyHomePage,
        signOnProvider: mapApiSignOnProvider(user.signOnProvider || null)
    };
}
